import type { ReactNode } from 'react';

interface ToggleRowProps {
  label: string;
  hint?: ReactNode;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}

/**
 * A labelled on/off switch.
 *
 * A real `<input type="checkbox">` under a styled track rather than a
 * `<button role="switch">`: the checkbox comes with the label association,
 * the keyboard behaviour and the screen-reader announcement already correct,
 * and everything this adds is paint. The whole row is the label, which on a
 * phone is the difference between a comfortable target and a 20-pixel one.
 */
export function ToggleRow({ label, hint, checked, disabled = false, onChange }: ToggleRowProps) {
  return (
    <label className={`toggle-row${disabled ? ' toggle-row--disabled' : ''}`}>
      <span className="toggle-row__text">
        <span className="toggle-row__label">{label}</span>
        {hint ? <span className="muted small">{hint}</span> : null}
      </span>
      <input
        type="checkbox"
        className="toggle-row__input"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className="toggle-row__track" aria-hidden="true">
        <span className="toggle-row__knob" />
      </span>
    </label>
  );
}
